import { Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useDealClientNames } from "../../hooks/useDealClientNames";
import { selectPendingFileRequests } from "../../utils/fileRequestQueue";

function timeAgo(createdat) {
  const ts = Number(createdat || 0);
  if (!ts) return "";
  const ms = Date.now() - ts;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}

export function subjectForDeal(deal, getClientName) {
  return (
    deal?.propertyaddress ||
    (getClientName && getClientName(deal)) ||
    deal?.clientLastName ||
    (deal?.dealtype ? `${deal.dealtype} request` : "Untitled deal")
  );
}

export const dealDetailLink = (deal) =>
  `/dashboard/deals-detail/${deal.createdat}`;

// A file request is a lead you're still working — it's only stored as a
// hidden deal so the Drive folder and portal forms have somewhere to live
// before there's a transaction. Sending View to the deal page leaked that
// plumbing at the user. Both pipelines already accept ?lead=<id> and open
// that lead's drawer, so this just points at the right one.
// Older requests were minted without a lead pointer — those still go to the
// deal page rather than a dead link.
export const fileRequestLink = (deal) => {
  if (!deal?.sourceLeadId) return dealDetailLink(deal);
  const pipeline = deal.dealtype === "Buyer" ? "buyers" : "listings";
  return `/dashboard/${pipeline}?lead=${deal.sourceLeadId}`;
};

function initialsFor(u) {
  const f = (u?.firstname || "").trim();
  const l = (u?.lastname || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (f || l || "?").slice(0, 2).toUpperCase();
}

export function Section({
  title,
  subtitle,
  emptyText,
  buttonLabel,
  deals,
  findAgent,
  subjectFor,
  viewLinkFor,
  onOpen,
  // The dashboard card scrolls inside a fixed height; the full-page view
  // (pages/file-requests.jsx) passes scroll={false} and lets the page scroll.
  scroll = true,
  // Where "View all →" goes. Only the dashboard card sets it — the full page
  // IS the "all".
  viewAllTo,
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 h-full">
      <div className="px-4 py-3 border-b border-black/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-amber-100 text-amber-700 rounded-md inline-flex items-center justify-center">
            <Clock size={14} strokeWidth={2.2} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              {title}
              {deals.length > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[11px] font-bold rounded-full px-2 py-0.5">
                  {deals.length}
                </span>
              )}
            </div>
            <div className="text-[11.5px] text-gray-500 mt-0.5">{subtitle}</div>
          </div>
        </div>
      </div>

      {deals.length === 0 ? (
        <div className="px-4 py-6 text-center text-gray-400 text-[13px]">
          {emptyText}
        </div>
      ) : (
        // Fixed height, scroll inside: the card stays one size however deep the
        // backlog gets. ~4 rows visible, the rest reachable by scrolling.
        <div className={scroll ? "max-h-[280px] overflow-y-auto overscroll-contain" : ""}>
          {deals.map((deal) => {
            const agent = findAgent(deal);
            const agentName = agent
              ? `${agent.firstname || ""} ${agent.lastname || ""}`.trim()
              : "Unknown agent";
            const subject = subjectFor(deal);
            return (
              <div
                key={deal.createdat || deal.id}
                className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-x-2.5 hover:bg-gray-50 transition"
              >
                <div className="w-7 h-7 rounded-full bg-slate-700 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {initialsFor(agent)}
                </div>
                {/* min-w-0 + truncate: the address gives way, the actions never
                   wrap to a second line — the wrap is what made every row twice
                   as tall (Henry, 2026-08-26: "Shrink this. its too big"). */}
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-900 truncate">
                    {subject}
                  </div>
                  {/* Wraps rather than overflowing: this card is a third of the
                     row on the Team Lead dashboard and full width elsewhere. */}
                  <div className="text-[11px] text-gray-500 flex items-center gap-x-1.5 mt-0.5 min-w-0">
                    <span className="truncate">{agentName}</span>
                    {deal?.dealtype && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded flex-shrink-0">
                        {deal.dealtype}
                      </span>
                    )}
                    <span className="text-gray-400 flex-shrink-0">
                      · {timeAgo(deal.createdat)}
                    </span>
                  </div>
                </div>
                {/* flex-shrink-0 is load-bearing. Without it both controls
                   shrink below their own text in a narrow card and collide
                   with the name. */}
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                  <Link
                    to={viewLinkFor(deal)}
                    className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 px-1.5 py-0.5 rounded border border-slate-300 hover:bg-white no-underline"
                  >
                    View
                  </Link>
                  <button
                    type="button"
                    onClick={() => onOpen?.(deal)}
                    className="text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-2 py-1 rounded flex items-center gap-0.5 cursor-pointer whitespace-nowrap"
                  >
                    {buttonLabel}
                    <ChevronRight size={11} className="flex-shrink-0" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {viewAllTo && deals.length > 0 && (
        <div className="px-4 py-2.5 text-center border-t border-slate-100">
          <Link
            to={viewAllTo}
            className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 no-underline"
          >
            View all {deals.length} →
          </Link>
        </div>
      )}
    </div>
  );
}

function PendingInboxPanel({
  deals,
  // File requests are soft-deleted the moment they're created, so they are NOT
  // in `deals` (getAlldealforAdmin drops soft-deleted rows). They arrive here on
  // their own, from getPendingFileRequests. See utils/fileRequestQueue.js.
  fileRequests: fileRequestsProp,
  users,
  currentUser,
  onOpen,
  onCompleteFileRequest,
}) {
  // Must run before the role check below — hooks can't sit after an early
  // return.
  const { getClientName } = useDealClientNames(
    [...(deals || []), ...(fileRequestsProp || [])],
  );

  const role = currentUser?.role;
  // Flagged agents ("Client care duties") get the file queue only — never
  // the Pending Approval queue, which leads into commission screens.
  const clientCareAgent =
    role === "Employee" && currentUser?.isClientCare === true;
  if (role !== "Admin" && role !== "Supervisor" && !clientCareAgent)
    return null;

  const allPending = (deals || [])
    .filter((d) => d?.status === "Pending")
    .sort((a, b) => Number(b?.createdat || 0) - Number(a?.createdat || 0));

  const turnIns = allPending.filter((d) => d?.requestType === "saleTurnIn");
  // Fall back to `deals` for any caller not yet passing the dedicated list, so
  // an un-wired screen degrades to the old behaviour instead of blowing up.
  const fileRequests = selectPendingFileRequests(fileRequestsProp || deals);

  const findAgent = (deal) =>
    (users || []).find(
      (u) => u?.id === deal?.agentData || u?.id === deal?.createdby,
    );

  // What the row is actually FOR. A listing has an address; a buyer has no
  // property yet, so every buyer row used to read the same "Buyer request"
  // with nothing to tell them apart — even though the client was right there
  // on the deal. Prefer the address, then the resolved client name, then the
  // last name we stored at intake (available immediately, before contacts
  // load), and only then the generic label.
  const subjectFor = (deal) => subjectForDeal(deal, getClientName);

  // Team Lead sees the file queue too. Without it a requested file had nowhere
  // to surface for them at all — the Deals Board hides file-requests parked in
  // CMAs for every role, so this queue is the only window onto them.
  const showFileRequests =
    role === "Admin" || role === "Supervisor" || clientCareAgent;
  const showApprovals = role === "Admin" || role === "Supervisor";

  return (
    <>
      {showFileRequests && (
        <Section
          title="File Requests"
          subtitle="Agents waiting on a file · click to tell them it's ready"
          emptyText="No files waiting to be built."
          buttonLabel="Mark Completed"
          deals={fileRequests}
          findAgent={findAgent}
          subjectFor={subjectFor}
          viewLinkFor={fileRequestLink}
          onOpen={onCompleteFileRequest || onOpen}
          viewAllTo="/dashboard/file-requests"
        />
      )}
      {showApprovals && (
        <Section
          title="Pending Approval"
          subtitle="Sale turn-ins · click to complete commission and approve"
          emptyText="No deals waiting for approval."
          buttonLabel="Review & Approve"
          deals={turnIns}
          findAgent={findAgent}
          subjectFor={subjectFor}
          viewLinkFor={dealDetailLink}
          onOpen={onOpen}
        />
      )}
    </>
  );
}

export default PendingInboxPanel;
